import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryParamsService } from '@services/query-params/query-params.service';
import { StudentWorksService } from '@services/student-works/student-works.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IAppState } from '../app.state';
import {
  AddStudentWork,
  AddStudentWorkFailure,
  AddStudentWorkSuccess,
  AddTemplateFailure,
  AddTemplateRequest,
  AddTemplateSuccess,
  AddWorksReview,
  AddWorksReviewSuccess,
  AutoAssignStudentWorksFailure,
  AutoAssignStudentWorksRequest,
  AutoAssignStudentWorksSuccess,
  DownloadAllWorks,
  DownloadWorks,
  DownloadWorksFailure,
  DownloadWorksSuccess,
  EditWorkReview,
  EditWorkReviewFailure,
  EditWorkReviewSuccess,
  EStudentWorksActions,
  GetStudentAllWorksStatsSuccess,
  GetStudentsWorks,
  GetStudentsWorksFailure,
  GetStudentsWorksStats,
  GetStudentsWorksSuccess,
  GetStudentWorksStatsFailure,
  GetStudentWorksStatsSuccess,
  GetTemplatesRequest,
  GetTemplatesSuccess,
  GetWorkReview,
  GetWorkReviewSuccess,
  PublishReviews,
  PublishReviewsFailure,
  PublishReviewsSuccess,
  TakeInCheckStudentWorksFailure,
  TakeInCheckStudentWorksRequest,
  TakeInCheckStudentWorksSuccess,
  TransferReviewersFailure,
  TransferReviewersRequest,
  TransferReviewersSuccess,
  UpdateStudentWork,
  UpdateStudentWorkSuccess,
} from './student-works.actions';
import { IReviewTemplates } from '@interfaces/IReviewTemplates';

@Injectable()
export class StudentWorksEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _studentWorksService: StudentWorksService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _toastr: ToastrService,
    private _qpService: QueryParamsService
  ) {}

  $createStudentWork = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddStudentWork>(EStudentWorksActions.AddStudentWork),
        map(action => action.work),
        switchMap(work => {
          return this._studentWorksService.addStudentWork(work).pipe(
            map(res => {
              this._store.dispatch(new AddStudentWorkSuccess());
              this._router.navigate([`/events/${res.event.id}`]);
              this._toastr.success('Работа успешно загружена');
              this._store.dispatch(
                new GetStudentsWorks({
                  student_id: String(res.student.id),
                  event_id: String(res.event.id),
                })
              );
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При загрузке работы возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new AddStudentWorkFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $updateStudentWork = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<UpdateStudentWork>(EStudentWorksActions.UpdateStudentWork),
        switchMap(work => {
          return this._studentWorksService
            .updateStudentWork(work.work, work.id)
            .pipe(
              map(res => {
                this._store.dispatch(new UpdateStudentWorkSuccess());
                this._router.navigate([`/events/${res.event.id}`]);
                this._toastr.success('Работа успешно загружена');
                this._store.dispatch(
                  new GetStudentsWorks({
                    student_id: String(res.student.id),
                    event_id: String(res.event.id),
                  })
                );
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При загрузке работы возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new AddStudentWorkFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $getStudentWorks = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetStudentsWorks>(EStudentWorksActions.GetStudentWorks),
        switchMap(action => {
          return this._studentWorksService.getStudentWorks(action.queries).pipe(
            map(res => {
              this._store.dispatch(new GetStudentsWorksSuccess(res));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось получить работы студента'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new GetStudentsWorksFailure());
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $getStudentWorksStats = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetStudentsWorksStats>(
          EStudentWorksActions.GetStudentWorksStats
        ),
        switchMap(action => {
          return this._studentWorksService
            .getStudentWorksStats(action.qParams)
            .pipe(
              map(res => {
                if (action.qParams && action.qParams.all) {
                  this._store.dispatch(new GetStudentAllWorksStatsSuccess(res));
                } else {
                  this._store.dispatch(new GetStudentWorksStatsSuccess(res));
                }
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось получить статистику по работам студентов'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new GetStudentWorksStatsFailure());
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $getTransferReviewers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<TransferReviewersRequest>(
          EStudentWorksActions.TransferReviewersRequest
        ),
        switchMap(action => {
          return this._studentWorksService
            .transferReviewers(action.reviewers, action.toUser)
            .pipe(
              map(() => {
                this._store.dispatch(new TransferReviewersSuccess());
                this._toastr.success('Проверяющие успешно переданы');
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При передаче проверяющих возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new TransferReviewersFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $addWorksReview = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddWorksReview>(EStudentWorksActions.AddWorksReview),
        switchMap(action => {
          return this._studentWorksService
            .addStudentWorksReview(action.works)
            .pipe(
              map(() => {
                this._store.dispatch(new AddWorksReviewSuccess());
                this._store.dispatch(
                  new GetStudentsWorksStats({ is_active: 'true' })
                );
                this._toastr.success(
                  'Вы успешно взяли выбранные домашки в проверку'
                );
                if (action.options?.afterLink) {
                  this._router.navigate([action.options?.afterLink]);
                }
                if (action.options?.changeParams) {
                  this._qpService.changeQueryParams(
                    action.options?.changeParams
                  );
                }
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При взятии работ в проверку возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $getWorkReview = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetWorkReview>(EStudentWorksActions.GetWorkReview),
        switchMap(action => {
          return this._studentWorksService.getStudentWorkReview(action.id).pipe(
            map(res => {
              this._store.dispatch(new GetWorkReviewSuccess(res));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось получить рецензию'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $editWorkReview = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<EditWorkReview>(EStudentWorksActions.EditWorkReview),
        switchMap(action => {
          return this._studentWorksService
            .updateStudentWorkReview(action.review)
            .pipe(
              map(review => {
                if (action.replaceState) {
                  this._store.dispatch(new EditWorkReviewSuccess(review));
                  return;
                }

                this._store.dispatch(new EditWorkReviewSuccess());
              }),
              catchError(err => {
                this._toastr.error(err.error.points);
                this._store.dispatch(new EditWorkReviewFailure());
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $publishReviews = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<PublishReviews>(EStudentWorksActions.PublishReviews),
        switchMap(action => {
          return this._studentWorksService
            .publishReviews(action.reviews, action.scheduled_datetime)
            .pipe(
              map(res => {
                this._toastr.success(`${res[0]?.message}`);
                if (action.options?.changeParams) {
                  this._qpService.changeQueryParams(
                    action.options?.changeParams
                  );
                }
                this._store.dispatch(new PublishReviewsSuccess(res));
              }),
              catchError(err => {
                this._toastr.error(err.error.reviews[0].message);
                this._store.dispatch(new PublishReviewsFailure());
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $downloadWorks = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DownloadWorks>(EStudentWorksActions.DownloadWorks),
        switchMap(action => {
          return this._studentWorksService
            .downloadStudentWorks(action.works)
            .pipe(
              map(res => {
                this._toastr.success(res.detail);
                this._store.dispatch(new DownloadWorksSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось скачать работы. Повторите попытку'
                  }`,
                  'Что-то пошло не так',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new DownloadWorksFailure());
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $downloadAllWorks = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DownloadAllWorks>(EStudentWorksActions.DownloadAllWorks),
        switchMap(action => {
          return this._studentWorksService
            .downloadAllWorks(action.queries)
            .pipe(
              map(res => {
                this._toastr.success(res.detail);
                this._store.dispatch(new DownloadWorksSuccess());
              }),
              catchError(err => {
                this._toastr.warning(
                  'Скачивание происходит только Вами взятых работ. Проверьте их наличие.'
                );
                this._toastr.error(
                  'Не удалось скачать работы. Повторите попытку.'
                );
                this._store.dispatch(new DownloadWorksFailure());
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $getTemplates = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GetTemplatesRequest>(
          EStudentWorksActions.GetReviewTemplatesRequest
        ),
        switchMap(() => {
          return this._studentWorksService.getReviewTemplates().pipe(
            map((templates: IReviewTemplates[]) => {
              this._store.dispatch(new GetTemplatesSuccess(templates));
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не удалось скачать работы. Повторите попытку'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new DownloadWorksFailure());
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $addTemplate = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddTemplateRequest>(
          EStudentWorksActions.AddReviewTemplateRequest
        ),
        switchMap(action => {
          return this._studentWorksService
            .getReviewTemplate(action.payload.id, action.payload.template_id)
            .pipe(
              map(() => {
                this._store.dispatch(new AddTemplateSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось применить шаблон. Повторите попытку'
                  }`,
                  'Ошибка',
                  { timeOut: 3500 }
                );
                this._store.dispatch(new AddTemplateFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $takeInCheck = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<TakeInCheckStudentWorksRequest>(
          EStudentWorksActions.TakeInCheckStudentWorksRequest
        ),
        switchMap(action => {
          return this._studentWorksService.takeInCheck(action.qParams).pipe(
            map(() => {
              this._store.dispatch(new TakeInCheckStudentWorksSuccess());
              this._store.dispatch(new GetStudentsWorksStats());

              if (action.options?.changeParams) {
                this._router.navigate([], {
                  relativeTo: this._route,
                  queryParams: { ...action.options?.changeParams },
                });
              }
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.message
                    ? err?.error?.message
                    : 'При взятии работ в проверку возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(new TakeInCheckStudentWorksFailure());
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $autoAssign = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AutoAssignStudentWorksRequest>(
          EStudentWorksActions.AutoAssignRequest
        ),
        switchMap(action => {
          return this._studentWorksService.autoAssignWorks(action.payload).pipe(
            map(() => {
              const event = action.payload.event;

              this._store.dispatch(new AutoAssignStudentWorksSuccess());
              this._router.navigate([], {
                relativeTo: this._route,
                queryParams: {
                  status: 'IN_REVIEW',
                  event__group_id: event.group?.id,
                  event_id: event.id,
                  page: 1,
                  page_size: 10,
                },
              });
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.message
                    ? err?.error?.message
                    : 'При автоматическом назначении работ возникла ошибка'
                }`,
                'Что-то пошло не так',
                { timeOut: 3500 }
              );
              this._store.dispatch(
                new AutoAssignStudentWorksFailure(err.errors)
              );
              return err.statusText;
            })
          );
        })
      );
    },
    { dispatch: false }
  );
}
